import { Component, inject, input, OnInit, output, ViewChild } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';

import { MicrosoftOnPremAdResource } from '../../../../../../shared/resources/microsoft-on-prem-ad.resource';
import { MicrosoftAttributes } from '../../../../../../../shared/models/generated/ziroModels';
import { ZiroFieldConfigs, ZiroFormConfig, ZiroFormsUpdate } from '../../../../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../../../../forms/fields/text/ziro-text.component';
import {
  HelpdeskAdAttributesEntity,
  HelpdeskAdAttributesFormComponent,
} from './helpdesk-ad-attributes-form/helpdesk-ad-attributes-form.component';
import { ZiroIcons } from '../../../../../../../shared/models/ziro-icons.enum';

@Component({
  selector: 'ziro-helpdesk-ad-attributes',
  templateUrl: './helpdesk-ad-attributes.component.html',
  standalone: false,
})
export class HelpdeskAdAttributesComponent implements OnInit {
  private _microsoftOnPremAdResource = inject(MicrosoftOnPremAdResource);

  @ViewChild(HelpdeskAdAttributesFormComponent) adAttributeForm: HelpdeskAdAttributesFormComponent;
  readonly adItemWasChanged = output<void>();
  readonly isZpcSyncWarningPresent = input<boolean>();
  readonly tenantId = input<string>();
  readonly onPremUpn = input<string>();
  readonly dialPlanId = input<number>();
  readonly mainNumber = input<string>();
  // Depending on range type, selectedNumber could be DID or EXT
  readonly selectedNumber = input<string>();
  readonly lineUriExtensionForDedicatedDid = input<string>();

  initialEntity: HelpdeskAdAttributesEntity;
  isLoading = true;
  formConfig: ZiroFormConfig;
  attributeFields: string[] = [];
  ziroIcons = ZiroIcons;
  isAnyAttributeVisible = true;
  private _existingAttributes: MicrosoftAttributes;

  ngOnInit(): void {
    this.init();
  }

  init(manuallyUpdatedExtensionValue?: string): void {
    this.isLoading = true;
    if (!this.isZpcSyncWarningPresent()) {
      const mainNumber = this.mainNumber();
      const lineUriExtensionForDedicatedDid = this.lineUriExtensionForDedicatedDid();
      forkJoin([
        this._microsoftOnPremAdResource.getOnPremAttributesFieldConfig(
          this.tenantId(),
          mainNumber ? mainNumber : this.selectedNumber(),
          lineUriExtensionForDedicatedDid
            ? manuallyUpdatedExtensionValue || lineUriExtensionForDedicatedDid
            : this.mainNumber()
              ? this.selectedNumber()
              : '',
        ),
        this._microsoftOnPremAdResource.getAdAttributes(this.tenantId(), this.onPremUpn(), this.dialPlanId()),
      ]).subscribe(([fieldConfig, existingAttributes]) => {
        if (existingAttributes) {
          this._existingAttributes = existingAttributes;
          this.attributeFields = Object.keys(fieldConfig.attributes);
          this.formConfig = {
            fields: this.attributeFields.reduce((acc: ZiroFieldConfigs, name) => {
              acc[name] = {
                label: name,
                required: fieldConfig.attributes[name].required,
                hidden: () => !fieldConfig.attributes[name].show,
                dataAutomation: name + '-input',
                defaultValue: () => fieldConfig.attributes[name].defaultValue,
                componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
              };
              return acc;
            }, {}),
          };
          // If all fields are hidden, initialEntity will be used to ensure we are saving the correct values
          this.initialEntity = this.attributeFields.reduce((acc: HelpdeskAdAttributesEntity, name) => {
            acc[name] = existingAttributes.writeDownListJson.some(
              (attr) => attr.attributeName === name && !!attr.attributeValue,
            )
              ? existingAttributes.writeDownListJson.find((attrib) => attrib.attributeName === name)?.attributeValue
              : fieldConfig.attributes[name].defaultValue;
            return acc;
          }, {});
          this.isAnyAttributeVisible = Object.entries(fieldConfig.attributes).some(
            (attr) =>
              attr[1].show ||
              Object.entries(this.initialEntity).some(
                (entry) => entry[0] === attr[0] && entry[1] !== attr[1].defaultValue,
              ),
          );
        } else {
          this.isAnyAttributeVisible = false;
        }
        this.isLoading = false;
      });
    } else {
      this.isLoading = false;
    }
  }

  onChildFormUpdate(event: ZiroFormsUpdate<HelpdeskAdAttributesEntity>): void {
    if (event.new !== event.old) {
      this.adItemWasChanged.emit();
    }
  }

  isFormValid(): boolean {
    if (!this.isAnyAttributeVisible) {
      return true;
    } else {
      return !!this.adAttributeForm && this.adAttributeForm?.isFormValid();
    }
  }

  submitAdAttributeForm(): Observable<void> {
    return !!this.adAttributeForm || !!this.initialEntity
      ? this._microsoftOnPremAdResource.putAdAttributes(
          this.tenantId(),
          this.onPremUpn(),
          this.dialPlanId(),
          this._getPayload(this.adAttributeForm ? this.adAttributeForm.entity : this.initialEntity, this.onPremUpn()),
        )
      : of(null);
  }

  private _getPayload(formData: HelpdeskAdAttributesEntity, userPrincipalName: string): MicrosoftAttributes {
    return {
      userPrincipalName,
      writeDownListJson: Object.entries(formData).map((entry) => {
        return {
          attributeName: entry[0],
          attributeValue: entry[1],
        };
      }),
    };
  }
}
